import { arrayToMap, EmptyMap, sum } from 'lib/array/arrayUtils'
import { isExperienceItem, isExperienceTicketViewItem, isTourV2ExperienceItem, isTourV2Item } from 'lib/checkout/checkoutUtils'
import { createSelector } from 'reselect'
import getExperienceItemsView from '../view/itemViews/getExperienceItemsView'

function getDiscountItemId(item: App.Checkout.AnyItem): string {
  if (isTourV2Item(item)) {
    return ('roomId' in item.occupancy && item.occupancy.roomId) ? item.occupancy.roomId as string : item.itemId
  }

  if (isTourV2ExperienceItem(item)) {
    return item.purchasableOption.fkExperienceId ?? item.itemId
  }

  if (isExperienceItem(item)) {
    // experiences items need to be handled separately as we process promos at ticket level
    // but experience items are at experience level (higher) and will never match
    return ''
  }

  if (isExperienceTicketViewItem(item)) {
    return item.transactionKey
  }

  return item.itemId
}

/**
 * Per-line discount for payment schedules, loyalty, etc.
 *
 * Contract (@luxuryescapes/contract-svc-promo Discount Response `promo.items[]`): `discountAmount` is the
 * promo discount for the item; `vouchersApplied` is mapped to `appliedVoucher` separately (`promo.voucherTotal`
 * aggregates vouchers). They are independent fields — do not assume `discountAmount` includes voucher amounts.
 */
function linePromoPlusVoucherDiscount(promoLine: App.Checkout.ItemDiscount | undefined): number | undefined {
  if (!promoLine) {
    return undefined
  }
  const voucherDiscount = sum((promoLine.appliedVoucher ?? []).map(v => v.amount))
  return (promoLine.discountAmount ?? 0) + voucherDiscount
}

const getAllItemsDiscountTotals = createSelector(
  (state: App.State) => state.checkout.promo?.items,
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getExperienceItemsView(state),
  (promoItems, cartItems, experienceViews): Map<string, number | undefined> => {
    if (!promoItems?.length) {
      return EmptyMap
    }
    const map = new Map<string, number | undefined>()

    const promoItemsById = arrayToMap(promoItems, item => item.itemId)
    cartItems.forEach((item) => {
      const itemId = getDiscountItemId(item)

      if (itemId) {
        map.set(item.itemId, linePromoPlusVoucherDiscount(promoItemsById.get(itemId)))
      }
    })

    experienceViews.data.forEach(view => {
      view.ticketViews.forEach(ticket => {
        // experiences promos are actually stored against transaction key, not item id
        // This is because they are 'fake' items and item id doesn't really exist at the ticket level
        const itemId = ticket.item.transactionKey
        map.set(ticket.item.itemId, linePromoPlusVoucherDiscount(promoItemsById.get(itemId)))
      })
    })

    if (!map.size) return EmptyMap

    return map
  },
)

export default getAllItemsDiscountTotals
